import { useState, type MouseEvent, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import {
  checkDeviceAlive,
  restartDevice,
  setNotifyWhenAlive,
  uninstallDevice,
  wakeDevice,
  type ActionResult,
} from '../api/inventoreeDeviceActions'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'

/**
 * Ce se intampla cand omul apasa o intrare din sertarul "Action" al unui rand.
 *
 * =============================================================================
 * DE CE E AICI SI NU IN CELULA
 * =============================================================================
 *
 * Celula vine gata facuta de la server (`OverviewActions`), ca in portal, si
 * `DataTable` o pune in `td` cu `dangerouslySetInnerHTML`. Nu exista deci
 * componente React pe care sa se agate un `onClick`: exista niste `<a>`-uri cu
 * clase (`wakeUpDevice`, `uninstallOd`, `checkAlive`) si o casuta
 * (`notifyAlive`). Portalul le prindea cu jQuery, la fiecare desenare a
 * tabelului, si le lega de doua ori - o data pentru lista, o data pentru randul
 * desfasurat - asa ca o apasare trimitea doua comenzi.
 *
 * Aici le prinde un singur ascultator, pe invelisul tabelului, dupa `closest()`:
 * randurile se pot redesena oricat, ascultatorul nu se muta si nu se dubleaza.
 *
 * =============================================================================
 * INTREBAREA DINAINTE RAMANE CEA DIN PORTAL
 * =============================================================================
 *
 * "On Lan WakeUp" si "Remove OptimumDesk" intrebau cu `confirm()` al
 * navigatorului - "Are you sure ?" si "Are you sure?" (da, unul cu spatiu
 * inaintea semnului, celalalt fara). "Restart device" deschidea o fereastra cu
 * mesajul, secundele si "force". "Check if alive" si bifa de instiintare nu
 * intrebau nimic. Se pastreaza intocmai: dezinstalarea agentului si repornirea
 * intrerup ziua de lucru a cuiva, si nu se face mai usor de apasat decat era.
 *
 * =============================================================================
 * RASPUNSUL SE CITESTE
 * =============================================================================
 *
 * Portalul arata "Wake up command has been sent." la ORICE raspuns cu 200, si
 * "No ad relay device!" la orice raspuns cu eroare, oricare ar fi fost ea.
 * Capetele v4 spun `ok` si, cand nu, `error` - iar mesajul aratat e chiar al
 * lor: o masina fara nicio adresa MAC nu mai raporteaza ca a fost trezita.
 */

const RESTART_DEFAULT = 'Please save your work. Windows will restart.'
const RESTART_TIMES = [0, 30, 60, 120]

function idOf(el: Element | null): number {
  const raw = el instanceof HTMLElement ? el.dataset.id : undefined
  const id = Number(raw)
  return Number.isFinite(id) && id > 0 ? id : 0
}

function failure(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'An error occured! Please try again..'
}

/** Un singur loc in care se hotaraste ce vede omul dupa o fapta. */
function report(result: ActionResult, sent: string): boolean {
  if (result.ok) {
    toast(sent, 'Success')
    return true
  }
  toast(result.error ?? 'Something went wrong', 'Error', 'error')
  return false
}

export interface ManagedRowActions {
  /** Se pune pe invelisul tabelului: `<div onClick={actions.onClick}>`. */
  onClick(event: MouseEvent<HTMLElement>): void
  /** Fereastra de repornire; se pune o data, langa tabel. */
  modal: ReactNode
}

/**
 * @param refresh chemat dupa "Check if alive", ca `mainDatatable.draw('page')`
 *                din portal: coloana "Last seen" isi schimba starea.
 */
export function useManagedRowActions(refresh: () => void): ManagedRowActions {
  const [restartFor, setRestartFor] = useState<number | null>(null)
  const [message, setMessage] = useState(RESTART_DEFAULT)
  const [seconds, setSeconds] = useState(0)
  const [force, setForce] = useState(false)
  const [sending, setSending] = useState(false)

  const closeRestart = () => setRestartFor(null)

  const onClick = (event: MouseEvent<HTMLElement>) => {
    const target = event.target
    if (!(target instanceof Element)) return

    /*
     * Bifa de instiintare se prinde tot pe CLIC, nu pe `onChange`. Celula vine
     * din `dangerouslySetInnerHTML`, deci casuta nu are fibra React, iar
     * `ChangeEventPlugin` nu scoate un `change` sintetic pentru un element pe
     * care React nu l-a desenat. Clicul urca insa pana la invelis ca oricare
     * altul - si tot pe clic citeste `ManagedPage` si casutele de selectie ale
     * randurilor. In clipa asta `checked` e deja starea NOUA.
     */
    if (target instanceof HTMLInputElement && target.classList.contains('notifyAlive')) {
      notifyToggled(target)
      return
    }

    const wake = target.closest('a.wakeUpDevice')
    if (wake) {
      event.preventDefault()
      const id = idOf(wake)
      /* Spatiul dinaintea semnului e al portalului, nu o scapare. */
      if (id > 0 && window.confirm('Are you sure ?')) {
        void wakeDevice(id)
          .then((r) => report(r, 'Wake up command has been sent.'))
          .catch((err: unknown) => toast(failure(err), 'Error', 'error'))
      }
      return
    }

    const restart = target.closest('a[data-target="#restartDeviceModal"]')
    if (restart) {
      event.preventDefault()
      const id = idOf(restart)
      if (id > 0) {
        setMessage(RESTART_DEFAULT)
        setSeconds(0)
        setForce(false)
        setRestartFor(id)
      }
      return
    }

    const uninstall = target.closest('a.uninstallOd')
    if (uninstall) {
      event.preventDefault()
      const id = idOf(uninstall)
      if (id > 0 && window.confirm('Are you sure?')) {
        void uninstallDevice(id)
          .then((r) => report(r, 'Uninstall command has been sent!'))
          .catch((err: unknown) => toast(failure(err), 'Error', 'error'))
      }
      return
    }

    const check = target.closest('a.checkAlive')
    if (check) {
      event.preventDefault()
      const id = idOf(check)
      if (id > 0) {
        void checkDeviceAlive(id)
          .then((r) => {
            if (report(r, 'Instruction sent.')) refresh()
          })
          .catch((err: unknown) => toast(failure(err), 'Error', 'error'))
      }
    }
  }

  const notifyToggled = (box: HTMLInputElement) => {
    const id = idOf(box)
    if (id < 1) return
    const on = box.checked
    /* Bifa se intoarce daca scrierea n-a mers: altfel ecranul ar spune ca omul
       va fi instiintat, iar randul din baza ar zice altceva. `change` pus la
       loc lasa `DataTable` sa refaca invelisul iCheck. */
    const revert = () => {
      box.checked = !on
      box.dispatchEvent(new Event('change', { bubbles: true }))
    }
    void setNotifyWhenAlive(id, on)
      .then((r) => {
        if (!report(r, 'Instruction sent.')) revert()
      })
      .catch((err: unknown) => {
        toast(failure(err), 'Error', 'error')
        revert()
      })
  }

  const submitRestart = () => {
    if (restartFor === null || sending) return
    const id = restartFor
    setSending(true)
    void restartDevice(id, { restart_message: message, restart_time: seconds, force_restart: force })
      .then((r) => report(r, 'Command sent!'))
      .catch((err: unknown) => toast(failure(err), 'Error', 'error'))
      .finally(() => {
        setSending(false)
        closeRestart()
      })
  }

  const modal = (
    <Modal
      id="restartDeviceModal"
      open={restartFor !== null}
      onClose={closeRestart}
      title="Restart device"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={closeRestart}>
            Close
          </button>
          <button type="button" className="btn btn-success goRestartDevice" disabled={sending} onClick={submitRestart}>
            <i className="fas fa-sync-alt"></i> Restart
          </button>
        </>
      }
    >
      <form
        id="restartDeviceForm"
        onSubmit={(e) => {
          e.preventDefault()
          submitRestart()
        }}
      >
        <div className="form-group">
          <label htmlFor="restartMessage">Message</label>
          <input
            id="restartMessage"
            className="form-control"
            type="text"
            name="restart_message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="restartTime">Time until restart (seconds)</label>
          <select
            id="restartTime"
            className="form-control"
            name="restart_time"
            value={String(seconds)}
            onChange={(e) => setSeconds(Number(e.target.value))}
          >
            {RESTART_TIMES.map((n) => (
              <option key={n} value={String(n)}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <SettingsCheck
            id="restartForce"
            name="force_restart"
            className="iCheckForce"
            value="1"
            checked={force}
            onChange={setForce}
            label="Force restart"
          />
        </div>
      </form>
    </Modal>
  )

  return { onClick, modal }
}
